import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import DescriptionIcon from '@mui/icons-material/Description';
import {
  PublicHeader,
  PublicFooter,
  VehicleCard,
  StatusIllustration,
  ProgressStepper,
  EstimatedReadyCard,
  CustomerNotesCard,
  JobsProgressCard,
  PartsListCard,
  PriceEstimateCard,
  WorkshopContactCard,
  ExtraWorkRequestCard,
  WorkOrderConfirmationCard,
  getStatusColor,
  theme as publicTheme,
} from './public';

// Create a separate axios instance for public API calls (no auth credentials)
const publicAxios = axios.create({
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

function PublicMaintenanceView() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMaintenance = async () => {
    try {
      const response = await publicAxios.get(`/api/public_maintenance/${token}/`);
      setMaintenance(response.data);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? t('public_maintenance.maintenance_not_found')
          : t('public_maintenance.error_loading')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaintenance();
  }, [token, t]);

  const handleExtraWorkResponded = () => {
    // Refresh maintenance data after customer responds to extra work request
    fetchMaintenance();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
          padding: 3,
        }}
      >
        <Typography variant="h5" color="error" sx={{ textAlign: 'center' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const statusColors = getStatusColor(maintenance.state);
  const showEstimatedReady = ['planned', 'in_progress', 'waiting', 'vehicle_in_workshop'].includes(maintenance.state);

  return (
    <Box data-testid="public-maintenance-page" sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader workshopName={maintenance.organization?.name} logoUrl={maintenance.organization?.logo_url} logoBgColor={maintenance.organization?.logo_bg_color} />

      <Container
        maxWidth="sm"
        sx={{
          py: 3,
          flex: 1,
        }}
      >
        <VehicleCard
          vehicle={maintenance.vehicle}
          maintenanceId={maintenance.work_number}
        />

        <StatusIllustration
          status={maintenance.state}
          statusColors={statusColors}
        />

        <ProgressStepper
          currentStatus={maintenance.state}
          statusColors={statusColors}
        />

        {/* Work Order Confirmation */}
        {['pending', 'confirmed', 'declined'].includes(maintenance.confirmation_status) && (
          <WorkOrderConfirmationCard
            maintenance={maintenance}
            token={token}
            onResponded={fetchMaintenance}
          />
        )}

        {/* Extra Work Requests awaiting customer approval */}
        {maintenance.extra_work_requests?.filter(r => r.status === 'pending').map(request => (
          <ExtraWorkRequestCard
            key={request.id}
            request={request}
            token={token}
            onResponded={handleExtraWorkResponded}
          />
        ))}

        {showEstimatedReady && maintenance.estimated_ready && (
          <EstimatedReadyCard estimatedReady={maintenance.estimated_ready} />
        )}

        {maintenance.show_description_to_customer && maintenance.description && (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: publicTheme.colors.surface,
              borderRadius: publicTheme.radius.xl,
              padding: '20px',
              boxShadow: publicTheme.shadows.md,
              marginBottom: '20px',
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontSize: '16px',
                fontWeight: '700',
                margin: '0 0 12px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <DescriptionIcon sx={{ color: publicTheme.colors.accent, fontSize: 20 }} />
              {t('public_maintenance.problem_description')}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                padding: '14px 16px',
                backgroundColor: '#dbeafe',
                borderRadius: publicTheme.radius.lg,
                borderLeft: `4px solid ${publicTheme.colors.accent}`,
              }}
            >
              {maintenance.description}
            </Typography>
          </Paper>
        )}

        {maintenance.notes?.length > 0 && (
          <CustomerNotesCard notes={maintenance.notes} />
        )}

        {maintenance.jobs?.length > 0 && (
          <JobsProgressCard jobs={maintenance.jobs} />
        )}

        {maintenance.parts?.length > 0 && (
          <PartsListCard parts={maintenance.parts} />
        )}

        <PriceEstimateCard
          parts={maintenance.parts}
          jobs={maintenance.jobs}
          maintenance={maintenance}
        />

        <WorkshopContactCard
          phone={maintenance.organization?.phone}
          address={maintenance.organization?.address_formatted || maintenance.organization?.address}
        />
      </Container>

      <PublicFooter />
    </Box>
  );
}

export default PublicMaintenanceView;
